import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import "./index.css";

import enthooImg from "../shared/enthoo-pro-2-server-edition-2.webp";
import mes2xImg from "../shared/mes2x-pc.webp";

const BUILDS = [
  {
    id: "enthoo",
    name: "Enthoo Pro 2 Server Edition",
    tagline: "Server-Class Custom AI Workstation",
    blurb:
      "Server-grade build for sustained multi-GPU training, large model fine-tuning, and 24/7 deep learning pipelines. Configure dual or quad NVIDIA RTX Pro GPUs, Threadripper PRO CPUs, ECC memory, and high-capacity NVMe storage.",
    image: enthooImg,
    href: "https://arsenalpc.com/product/custom-ai-workstations/enthoo-pro-2-server-edition-custom-ai-workstation/"
  },
  {
    id: "mes2x",
    name: "Meshify 2XL",
    tagline: "Dual-GPU Liquid-Cooled AI Workstation",
    blurb:
      "Dual graphics card AI workstation with custom liquid cooling, tuned for local LLM inference, fine-tuning, and generative AI workflows. Run dual RTX 5090 or RTX Pro 6000 configurations for RAG pipelines and Stable Diffusion training.",
    image: mes2xImg,
    href: "https://arsenalpc.com/product/custom-ai-workstations/mes2x-dual-gpu-ai-custom-workstation/"
  }
];

export default function App() {
  const rootRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const inView = useInView(rootRef, { margin: "-10% 0px", once: true });
  const shouldAnimate = inView && !prefersReducedMotion;

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 18 },
    animate: shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }
  });

  return (
    <section ref={rootRef} id="customize" className="pb">
      <div className="pb-inner">
        <div className="pb-header">
          <motion.span className="pb-eyebrow" {...fade(0)}>
            <span className="pb-eyebrow-mark" />
            Custom-Built AI Workstations
          </motion.span>
          <motion.h2 className="pb-title" {...fade(0.1)}>
            Configure the AI Workstation built for{" "}
            <span className="pb-title-accent">your workload</span>
          </motion.h2>
        </div>

        <div className="pb-grid">
          {BUILDS.map((build, i) => (
            <motion.a
              key={build.id}
              className="pb-card"
              href={build.href}
              initial={{ opacity: 0, y: 24 }}
              animate={
                shouldAnimate
                  ? { opacity: 1, y: 0 }
                  : { opacity: 1, y: 0 }
              }
              transition={{
                duration: 0.7,
                delay: 0.3 + i * 0.12,
                ease: [0.22, 1, 0.36, 1]
              }}
            >
              <div className="pb-card-image-wrap">
                <div className="pb-card-glow" aria-hidden="true" />
                <img
                  src={build.image}
                  alt={`${build.name} ${build.tagline}`}
                  className="pb-card-image"
                  loading="lazy"
                />
              </div>

              <div className="pb-card-body">
                <div className="pb-card-text">
                  <h3 className="pb-card-name">{build.name}</h3>
                  <p className="pb-card-tagline">{build.tagline}</p>
                  <p className="pb-card-blurb">{build.blurb}</p>
                </div>

                <span className="pb-card-cta">
                  Customize
                  <span className="pb-card-cta-arrow" aria-hidden="true">→</span>
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
